import React, { useState } from 'react';
import { ChevronDown, ChevronRight, CheckSquare } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../common';
import { formatV2Timestamp } from '../../utils/v2DateTime';
import ProblemNumberMap from './ProblemNumberMap';

/**
 * 검수 구역 (개요 ④ 단계 본문) — 매칭 결과를 훑어보고 검수 화면을 여는 자리.
 *
 * 화면에 두는 것: 문제 매칭 결과(reconcile_report) 요약 · 인덱스 지도 · [검수 시작].
 * 문제 목록(필터+표)은 검수 화면 좌측 탭으로 옮겼다 — 원본과 목록을 한 화면에서 오가는 편이
 * 목록 탭과 모달을 왕복하는 것보다 짧기 때문이다.
 *
 * 목록 상태는 여기가 갖지 않는다. ④가 접히면 이 구역이 통째로 언마운트되므로,
 * 여기에 상태를 두면 접을 때마다 목록이 날아가고 다른 단계에서 검수를 열 수도 없다.
 * 상태는 패널 최상위의 useOcrProblemList가 쥐고, 이 구역은 그 결과를 읽기만 한다.
 *
 * @param {string|number} projectId
 * @param {object|null} reconcile  reconcile_report (bind 응답 → 부모 보관, 없으면 배너 생략)
 * @param {number} reloadKey  매칭·분석 완료 등으로 인덱스 지도를 다시 읽어야 할 때 증가
 * @param {boolean} closed  프로젝트 종료(status>=500) — 인덱스 수정을 막는다
 * @param {object} list  useOcrProblemList 반환값
 * @param {string} hint  이 단계가 무엇인지 — [검수 시작] 바로 위에 둔다. 단계 맨 아래(StepActions)에
 *                       두면 인덱스 지도와 버튼을 지나 한참 내려가야 읽혀, 누르기 전에 읽을 말이
 *                       누른 뒤에 나온다. 문장 자체는 다른 단계의 안내와 함께 패널이 쥔다
 */

const NUM_CAP = 50; // 백엔드가 인덱스 나열을 50개로 캡

// 인덱스 배열 표시: 50개 캡에 도달하면 "50+ …"
function numListText(nums) {
  const arr = Array.isArray(nums) ? nums.filter((n) => typeof n === 'number') : [];
  if (arr.length === 0) return '없음';
  const capped = arr.length >= NUM_CAP;
  return arr.join(', ') + (capped ? ` … (${NUM_CAP}+)` : '');
}

function ScopeSummaryRow({ scopeKey, scope }) {
  const [open, setOpen] = useState(false);
  const gaps = Array.isArray(scope.body_gaps) ? scope.body_gaps.filter((n) => typeof n === 'number') : [];
  const orphans = Array.isArray(scope.orphan_numbers) ? scope.orphan_numbers : [];
  const range = Array.isArray(scope.body_range) && scope.body_range.length === 2
    ? `${scope.body_range[0]}~${scope.body_range[1]}`
    : '—';
  const hasDetail = gaps.length > 0
    || (scope.missing_answer_numbers || []).length > 0
    || (scope.missing_solution_numbers || []).length > 0
    || orphans.length > 0;

  return (
    <div className="text-sm text-gray-700">
      <button
        type="button"
        onClick={() => hasDetail && setOpen((v) => !v)}
        className={`flex w-full items-center gap-1 text-left ${hasDetail ? 'cursor-pointer' : 'cursor-default'}`}
      >
        {hasDetail ? (
          open ? <ChevronDown className="size-3.5 shrink-0 text-gray-400" /> : <ChevronRight className="size-3.5 shrink-0 text-gray-400" />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <span className="min-w-0">
          {scopeKey !== '__single__' && <span className="mr-1 font-medium text-gray-900">[{scopeKey}]</span>}
          본문 {scope.body_count ?? 0} · 정답 {scope.answer_count ?? 0} · 풀이 {scope.solution_count ?? 0}
          <span className="mx-1.5 text-gray-300">|</span>
          번호 {range} · 빠진 번호 {gaps.length >= NUM_CAP ? `${NUM_CAP}+` : gaps.length}개 · 본문 없는 번호 {orphans.length}개
        </span>
      </button>
      {open && (
        <div className="ml-5 mt-1 space-y-0.5 text-xs text-gray-500">
          {gaps.length > 0 && <div>빠진 본문 번호: {numListText(gaps)}</div>}
          {(scope.missing_answer_numbers || []).length > 0 && (
            <div>정답 없는 번호: {numListText(scope.missing_answer_numbers)}</div>
          )}
          {(scope.missing_solution_numbers || []).length > 0 && (
            <div>풀이 없는 번호: {numListText(scope.missing_solution_numbers)}</div>
          )}
          {orphans.length > 0 && <div>정답·풀이만 있고 본문이 없는 번호: {numListText(orphans)}</div>}
        </div>
      )}
    </div>
  );
}

// onOpenProblem — 문제를 여는 길을 밖에서 받는다. ④에서 열면 OCR 결과 얼굴, ⑤에서 열면 분석 얼굴이라
// 「어느 자리에서 열었는가」를 이 구역이 알 필요는 없다. 안 주면 종전대로 list.openById 를 쓴다.
function OcrReviewSection({ projectId, reconcile, reloadKey, closed = false, list, hint = null, onOpenProblem = null }) {
  const { getAccessToken } = useAuth();
  const scopes = reconcile?.scopes && typeof reconcile.scopes === 'object' ? reconcile.scopes : null;

  return (
    <div className="space-y-3">
      {/* 문제 매칭 결과 배너 — 서버 필드 이름은 reconcile_report 이지만 화면에는 「조율」을 쓰지 않는다.
          번역어라 뜻이 안 통한다. 이 표가 실제로 말하는 것은 「번호마다 본문·정답·풀이의 짝이 맞는가」다. */}
      {scopes && Object.keys(scopes).length > 0 && (
        <div className="space-y-1 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <div className="text-xs font-medium text-gray-500">
            문제 매칭 결과{reconcile.generated_at ? ` · ${formatV2Timestamp(reconcile.generated_at, 'dateTimeSeconds')}` : ''}
          </div>
          {Object.entries(scopes).map(([key, scope]) => (
            <ScopeSummaryRow key={key} scopeKey={key} scope={scope} />
          ))}
        </div>
      )}

      {/* 인덱스 지도 — 겹친 인덱스·빈 인덱스는 프로젝트 전체를 봐야 알 수 있어 여기서만 전량을 읽는다 */}
      <ProblemNumberMap
        projectId={projectId}
        reloadKey={reloadKey}
        closed={closed}
        onChanged={list.reload}
        onOpenProblem={onOpenProblem || list.openById}
        getAccessToken={getAccessToken}
      />

      {/* 안내는 버튼 줄 위에 한 줄을 통째로 갖는다 — StepActions와 같은 배치다(글 위, 동작 아래) */}
      {hint && <p className="text-xs text-gray-400">{hint}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-400">
          {list.loading ? '문제를 세는 중…' : list.loadError || `검수 대상 ${list.totalItems}건`}
        </span>
        <Button
          size="sm"
          className="ml-auto"
          onClick={() => list.openReview()}
          disabled={list.loading || list.items.length === 0}
          icon={<CheckSquare className="size-4" />}
        >
          검수 시작
        </Button>
      </div>
    </div>
  );
}

export default OcrReviewSection;
